/* UwU Rewards landing page — the membership-only join button.
 *
 * Fero: "give people the option to subscribe ... by only subscribing to the membership even if they
 * don't shop for more items. So that button should probably at the moment just go straight to
 * checkout maybe?" The "maybe" is doing work: that is the outcome he wants, not the mechanism.
 *
 * WHAT IT DOES
 *   cart empty      add the membership, go to /checkout   — one line, $0.00, exactly the ask
 *   cart has items  add the membership, go to /cart       — see below
 *
 * ⛔ NOT a cart permalink (/cart/{variant}:1). That REPLACES the cart rather than adding to it, so
 * a shopper with a basket would have it silently discarded on the way to checkout.
 *
 * ⭐ WHY IT ADDS IN BOTH CASES, WHICH IS A DEPARTURE
 * The proposal was: non-empty cart -> send them to the cart page and let the existing flow offer the
 * membership there. It does not survive the decline case. _uwu_rewards_offered is write-once, so a
 * shopper who declined the auto-add earlier gets no second offer — they would click "Start your free
 * trial", land on /cart, and find "Pay as Guest" selected and nothing added. The button would have
 * lied. Adding in both cases keeps the button honest; the destination is what changes, and a shopper
 * with items lands on the cart page where the choice table lets them reverse it immediately.
 *
 * An explicit click here deliberately supersedes an earlier decline. That is the shopper asking a
 * second time, on a page built to ask.
 *
 * ⚠️ Interaction with assets/uwu-membership-autoadd.js, which wraps fetch and watches /cart/add.js:
 * its shouldAdd() returns false when the incoming variant IS the membership, so this cannot trigger
 * a second add; and reconcileStranded() cannot fire either, since it needs a Simplee membership
 * arriving or already in the cart. This add passes through untouched.
 */
(function () {
  'use strict';

  /* Run once per document however many times this file is emitted. Three sections can each carry a
   * CTA, and a duplicate <script> executes twice — the bug this repo hit in #88, where two module
   * instances could not see each other's in-flight state and one shopper got two lines. A guard that
   * depends on remembering not to emit twice is not a guard. */
  if (window.__uwuRewardsJoinLoaded) return;
  window.__uwuRewardsJoinLoaded = true;

  var busy = false;

  function readConfig(el) {
    try {
      var parsed = JSON.parse(el.getAttribute('data-uwu-join-config') || '{}');
      if (!parsed.variantId || !parsed.sellingPlanId) return null;
      return { variantId: String(parsed.variantId), sellingPlanId: String(parsed.sellingPlanId) };
    } catch (e) {
      return null;
    }
  }

  /* The Simplee product and variant ids, as a LIST rather than an answer — snippets/
   * uwu-other-membership.liquid with mode: 'ids'. Single-sourced: the ids are store-scoped, so a
   * hardcoded copy here would degrade to "no match" on any store whose ids differ, and "no match"
   * is the ADDING direction. */
  function readOtherIds(el) {
    try {
      var parsed = JSON.parse(el.getAttribute('data-uwu-other-config') || '{}');
      return {
        products: Array.isArray(parsed.products) ? parsed.products.map(String) : [],
        variants: Array.isArray(parsed.variants) ? parsed.variants.map(String) : []
      };
    } catch (e) {
      return { products: [], variants: [] };
    }
  }

  function cartHasOtherMembership(items, other) {
    if (!other.products.length && !other.variants.length) return false;
    return items.some(function (i) {
      return other.products.indexOf(String(i.product_id)) !== -1 ||
             other.variants.indexOf(String(i.variant_id)) !== -1;
    });
  }

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' responded ' + r.status);
      return r.json();
    });
  }

  /* Releases the control and announces the reason.
   *
   * ⚠️ Resetting busy/disabled matters now in a way it did not before. The decline used to navigate
   * away, so leaving the button disabled was invisible; staying on the page means a shopper who
   * empties their cart in another tab and comes back would otherwise find a dead button with no
   * explanation. The status node is a live region, so assigning textContent announces it. */
  /* Scoped to the clicked button, not the document. Only one join-mode CTA renders today, which is
   * the only reason a document-wide lookup was ever right — and the handler below is delegated
   * precisely because the CTA can render in more than one section. A second join-mode render would
   * have put every message on the first node, potentially a screen away from the button pressed. */
  function statusFor(button) {
    var root = button.closest('[data-uwu-join-root]');
    return root ? root.querySelector('[data-uwu-join-status]') : null;
  }

  function decline(button, message) {
    busy = false;
    button.removeAttribute('aria-busy');
    button.disabled = false;

    var status = statusFor(button);
    if (status) status.textContent = message;
  }

  async function join(button) {
    if (busy) return;
    var cfg = readConfig(button);
    // No usable ids means the membership did not resolve. Liquid already declines to render the
    // button in that case; this is the belt for a stale or hand-edited DOM.
    if (!cfg) return;

    busy = true;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    // Clear any previous decline so a live region cannot re-announce a stale reason.
    var status = statusFor(button);
    if (status) status.textContent = '';

    try {
      // Read the cart BEFORE adding, so "was the shopper already carrying a basket" is answered
      // about the cart they arrived with rather than the one we just changed.
      var cart = await fetch('/cart.js?ts=' + Date.now(), {
        credentials: 'same-origin',
        cache: 'no-store'
      }).then(function (r) { return r.json(); });

      var items = Array.isArray(cart.items) ? cart.items : [];

      /* ⛔ THE CLICK-TIME GUARD, and the reason it cannot live at render time.
       *
       * The page's other UwU+/VIP check reads customer TAGS when the page is built. Tags answer a
       * question about the customer and do not change mid-session. This answers a question about
       * the CART, which changes after the page is rendered — and that is exactly order #163030: an
       * untagged shopper puts UwU+ in the cart, then clicks this button, and gets a second
       * membership beside it.
       *
       * ⚠️ Nothing downstream catches this. A direct add of the Rewards variant makes the global
       * wrapper's shouldAdd() return at its incoming-variant check, and reconcileStranded() then
       * returns without reading the cart at all, because for an untagged shopper nothing is
       * "arriving" and hasOtherMembership is false. This check is the only thing standing there.
       *
       * DECLINING. It adds nothing, says why in place, and stays on the page. It used to hop to
       * /cart instead, and that hop only existed because there was no approved wording — the cart
       * was the one place the reason was visible, by showing the membership already held. With the
       * reason stated outright the hop costs more than it gives: it throws a shopper off a landing
       * page in response to a click that changed nothing, and the cart is one icon away regardless.
       *
       * The wording is read from the button rather than written here, so the copy stays in the
       * template layer with the rest of the page's. */
      if (cartHasOtherMembership(items, readOtherIds(button))) {
        var reason = button.getAttribute('data-uwu-declined-text');
        if (reason) {
          decline(button, reason);
        } else {
          /* No wording to show. Falling back to an empty message would restore exactly the silent
           * decline this replaced, so it falls back to the BEHAVIOUR that preceded the wording
           * instead: the hop to /cart, where the membership already held is on screen and the
           * shopper can at least see why nothing happened. Degraded, never silent. */
          console.warn('[UwU Rewards] no decline wording on the join button; falling back to /cart');
          window.location.href = '/cart';
        }
        return;
      }

      /* "Does the shopper already have a basket" must ignore OUR OWN line. The cart auto-add puts
       * the membership in the moment anything else enters the cart, so a shopper arriving here from
       * a product page frequently has membership-only-plus-one-product. Counting the membership as
       * a basket item would route a membership-only cart to /cart instead of checkout. */
      var others = items.filter(function (i) { return String(i.variant_id) !== cfg.variantId; });
      var hadItems = others.length > 0;

      /* ⚠️ And do not add a second one. Found by testing: with the auto-add already having placed
       * the membership, clicking this button took the line to quantity 2. /cart/add.js increments an
       * existing line rather than replacing it. That is financially inert — the app hardcodes
       * quantity 1 on the contract — but it is the visible ×2 symptom this repo already fixed once
       * in #88, and a shopper seeing it has no way to know it is harmless. */
      var already = items.some(function (i) { return String(i.variant_id) === cfg.variantId; });

      if (!already) {
        // selling_plan is REQUIRED: the variant is requiresSellingPlan, so a plan-less add is
        // rejected rather than silently creating a one-off purchase of a subscription product.
        await post('/cart/add.js', {
          id: Number(cfg.variantId),
          quantity: 1,
          selling_plan: Number(cfg.sellingPlanId)
        });
      }

      window.location.href = hadItems ? '/cart' : '/checkout';
    } catch (e) {
      console.warn('[UwU Rewards] could not start the trial', e);
      // Put the control back rather than leaving a dead button under the shopper's cursor.
      busy = false;
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  // Delegated, because the CTA can render in more than one section and sections are re-rendered.
  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest && event.target.closest('[data-uwu-join]');
    if (!button) return;
    event.preventDefault();
    join(button);
  });
})();
